/*
This hook allows delaying certain events, so that they can be triggered
right before or right after other events.
Before rules structure example: {
  <current event type>: <event type, after which the current event should be triggered>
}
Queue structure example: {
  <current event type>: [<list of enqueued events to be triggered before this event>]
}
 */
import { default as useRefState } from './useRefState'
import { pushToDataLayer } from '../utils'
import { GTMEvent, EventQueue } from '../GTM.d'

// These events (object keys) are fired, they should be delayed
// until another event (object values) is fired
const defaultEventQueueAfterRules = {
    category: 'pageview',
    product: 'pageview',
    purchase: 'pageview',
    search: 'pageview',
    checkout: 'pageview'
}
const defaultEventQueueBeforeRules = {}

type EventQueueSetter = (queue: EventQueue) => void

export type EventQueueData = {
    eventHistory: () => string[]
    resetQueue: () => void
    checkQueueBeforeEvent: (data?: GTMEvent) => boolean
    checkQueueAfterEvent: (data?: GTMEvent) => void
    dequeueNextPageEvent: (eventName: string) => void
}

export const getQueueOrPushEvent = (
    checkQueueBeforeEvent: (data?: GTMEvent) => boolean,
    checkQueueAfterEvent: (data?: GTMEvent) => void
) => {
    return (data?: GTMEvent): void => {
        if (!data) {
            return
        }
        const isQueued = checkQueueBeforeEvent(data)
        if (!isQueued) {
            pushToDataLayer(data)
        }
        checkQueueAfterEvent(data)
    }
}

export const useEventQueue = (
    customBeforeRules = {},
    customAfterRules = {},
    delayToNextPageEvents: string[] = []
): EventQueueData => {
    const beforeRules = {
        ...defaultEventQueueBeforeRules,
        ...customBeforeRules
    }
    const afterRules = { ...defaultEventQueueAfterRules, ...customAfterRules }
    const [eventHistory, setEventHistory] = useRefState<string[]>([])
    const [nextPageEvents, setNextPageEvents] = useRefState<GTMEvent[]>([])
    const [beforeQueue, setBeforeQueue] = useRefState<EventQueue>({})
    const [afterQueue, setAfterQueue] = useRefState<EventQueue>({})

    // Add event to the history, add to the queue if needed
    const enqueueEvent = (
        event: string,
        data: GTMEvent,
        queue: EventQueue,
        setQueue: EventQueueSetter,
        rules: Record<string, string>,
        ignoreHistory = false
    ) => {
        if (typeof rules[event] === 'undefined') {
            return false
        }

        const dependentEventType = rules[event]
        if (!ignoreHistory && eventHistory().includes(dependentEventType)) {
            return false
        }
        const updatedQueue = { ...queue }
        const dependentEventQueue = updatedQueue[dependentEventType]
            ? updatedQueue[dependentEventType]
            : []
        dependentEventQueue.push(data)
        updatedQueue[dependentEventType] = dependentEventQueue
        setQueue(updatedQueue)
        return true
    }

    // Push queued events and remove from the queue
    const dequeueEvents = (
        event: string,
        data: GTMEvent,
        queue: EventQueue,
        setQueue: EventQueueSetter
    ) => {
        if (typeof queue[event] === 'undefined') {
            return
        }

        queue[event].forEach((queuedAction) => pushToDataLayer(queuedAction))

        const updatedQueue = { ...queue }
        delete updatedQueue[event]
        setQueue(updatedQueue)
    }

    const enqueueOnNextPage = (event: string, data: GTMEvent) => {
        if (
            !Array.isArray(delayToNextPageEvents) ||
            !delayToNextPageEvents.includes(event)
        ) {
            return false
        }
        const updatedNextPageEvents = nextPageEvents()
        updatedNextPageEvents.push(data)
        setNextPageEvents(updatedNextPageEvents)
        return true
    }

    // Return a flag that notifies whether this action is queued, or should be tracked now
    const checkQueueBeforeEvent = (data?: GTMEvent) => {
        if (!data) {
            return false
        }
        const { event } = data
        if (!event) {
            return false
        }

        const queuedBefore = enqueueEvent(
            event,
            data,
            beforeQueue(),
            setBeforeQueue,
            beforeRules
        )
        const queuedAfter = enqueueEvent(
            event,
            data,
            afterQueue(),
            setAfterQueue,
            afterRules
        )
        const queuedOnNextPage = enqueueOnNextPage(event, data)
        dequeueEvents(event, data, beforeQueue(), setBeforeQueue)
        return queuedBefore || queuedAfter || queuedOnNextPage
    }
    const checkQueueAfterEvent = (data?: GTMEvent) => {
        if (!data) {
            return
        }
        const { event } = data
        if (!event) {
            return
        }
        dequeueEvents(event, data, afterQueue(), setAfterQueue)
        const updatedHistory = eventHistory()
        updatedHistory.push(event)
        setEventHistory(updatedHistory)
    }

    const resetQueue = () => {
        setEventHistory([])
        setBeforeQueue({})
        setAfterQueue({})
        nextPageEvents().forEach((data) => {
            const { event } = data
            // Try queueing delayed events by existing rules
            const queuedBefore = enqueueEvent(
                event,
                data,
                beforeQueue(),
                setBeforeQueue,
                beforeRules,
                true
            )
            const queuedAfter = enqueueEvent(
                event,
                data,
                afterQueue(),
                setAfterQueue,
                afterRules,
                true
            )
            // If event doesn't match any rule, push it after pageview by default
            if (!queuedBefore && !queuedAfter) {
                const defaultRule = { [event]: 'pageview' }
                enqueueEvent(
                    event,
                    data,
                    afterQueue(),
                    setAfterQueue,
                    defaultRule,
                    true
                )
            }
        })
        setNextPageEvents([])
    }

    // In some cases, we may have a need to dequeue a next page event immediately
    const dequeueNextPageEvent = (eventName = '') => {
        if (!eventName) {
            return
        }
        const eventsToKeep: GTMEvent[] = []
        nextPageEvents().forEach((data) => {
            const { event } = data
            if (eventName !== event) {
                eventsToKeep.push(data)
                return
            }
            pushToDataLayer(data)
        })
        setNextPageEvents(eventsToKeep)
    }

    return {
        eventHistory,
        resetQueue,
        checkQueueBeforeEvent,
        checkQueueAfterEvent,
        dequeueNextPageEvent
    }
}
